import React, { useEffect, useState } from "react";
import { Chart } from "primereact/chart";
import { handleGetRequest } from "../../services/GetTemplate";
import styles from "./UserAnalytics.module.scss";
import { FiUsers, FiTrendingUp, FiAward, FiCalendar } from "react-icons/fi";

const UserAnalytics = () => {
    const [signupData, setSignupData] = useState(null);
    const [topShippers, setTopShippers] = useState([]);
    const [roleBreakdown, setRoleBreakdown] = useState(null);
    const [itemShifts, setItemShifts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Date range states
    const [dateRange, setDateRange] = useState("month"); // week, month, year, custom
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

    // Get current date
    const getCurrentDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    // Get date range based on selection
    const getDateRange = () => {
        const today = new Date();
        let startDate, endDate;

        switch (dateRange) {
            case "week":
                // Get first day of current week (Monday)
                startDate = new Date(today);
                startDate.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
                endDate = new Date();
                break;
            case "month":
                // Get first day of current month
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = new Date();
                break;
            case "year":
                // Get first day of current year
                startDate = new Date(today.getFullYear(), 0, 1);
                endDate = new Date();
                break;
            case "custom":
                startDate = customStartDate ? new Date(customStartDate) : new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = customEndDate ? new Date(customEndDate) : new Date();
                break;
            default:
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = new Date();
        }

        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        };
    };

    useEffect(() => {
        fetchUserAnalytics();
    }, [dateRange, customStartDate, customEndDate]);

    const fetchUserAnalytics = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const { startDate, endDate } = getDateRange();
            
            // Fetch all analytics data with date range
            const [signups, shippers, roles, shifts] = await Promise.all([
                handleGetRequest(`/analytics/users/signups?period=daily&startDate=${startDate}&endDate=${endDate}`).catch(e => {
                    console.error("Signups error:", e);
                    return null;
                }),
                handleGetRequest(`/analytics/users/top-shippers?limit=10&timeframe=all&startDate=${startDate}&endDate=${endDate}`).catch(e => {
                    console.error("Top shippers error:", e);
                    return null;
                }),
                handleGetRequest(`/analytics/users/role-breakdown`).catch(e => {
                    console.error("Role breakdown error:", e);
                    return null;
                }),
                handleGetRequest(`/analytics/users/most-shifts?limit=10&startDate=${startDate}&endDate=${endDate}`).catch(e => {
                    console.error("Item shifts error:", e);
                    return null;
                }),
            ]);

            // Process signup data for chart
            if (signups?.data) {
                // Group by date and count
                const groupedData = {};
                signups.data.forEach(item => {
                    const date = item._id || 'Unknown';
                    groupedData[date] = item.count;
                });

                // Sort dates
                const sortedDates = Object.keys(groupedData).sort();

                setSignupData({
                    labels: sortedDates,
                    datasets: [
                        {
                            label: "New Signups",
                            data: sortedDates.map(date => groupedData[date]),
                            fill: false,
                            borderColor: "#667eea",
                            backgroundColor: "rgba(102, 126, 234, 0.1)",
                            tension: 0.4,
                            borderWidth: 2,
                        }
                    ]
                });
            }

            setTopShippers(shippers?.data || []);
            
            // Process role breakdown for pie chart
            if (roles?.data) {
                setRoleBreakdown({
                    labels: roles.data.map(item => item.role),
                    datasets: [
                        {
                            data: roles.data.map(item => item.count),
                            backgroundColor: [
                                "#667eea",
                                "#10b981",
                                "#f59e0b",
                                "#ef4444",
                                "#8b5cf6",
                            ],
                            borderColor: "#fff",
                            borderWidth: 2,
                        }
                    ]
                });
            }

            // Handle item shifts
            if (shifts?.data && shifts.data.length > 0) {
                setItemShifts(shifts.data);
            } else {
                setItemShifts([]);
                console.warn("No shifted items data available");
            }

        } catch (error) {
            console.error("Error fetching user analytics:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDateRangeChange = (range) => {
        setDateRange(range);
        if (range !== "custom") {
            setShowCustomDatePicker(false);
        }
    };

    const handleCustomDateSubmit = () => {
        if (customStartDate && customEndDate) {
            if (new Date(customStartDate) > new Date(customEndDate)) {
                setError("Start date must be before end date");
                return;
            }
            setShowCustomDatePicker(false);
            setDateRange("custom");
        } else {
            setError("Please select both start and end dates");
        }
    };

    const chartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        plugins: {
            legend: {
                labels: {
                    color: "#495057",
                    font: { size: 12 },
                },
            },
        },
        scales: {
            x: {
                ticks: { color: "#495057" },
                grid: { color: "#ebedef" },
            },
            y: {
                ticks: { color: "#495057" },
                grid: { color: "#ebedef" },
            },
        },
    };

    const pieOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                labels: {
                    color: "#495057",
                    font: { size: 12 },
                    padding: 15,
                },
                position: "bottom",
            },
        },
    };

    const { startDate, endDate } = getDateRange();

    if (loading) {
        return <div className={styles.loading}>Loading User Analytics...</div>;
    }

    return (
        <div className={styles.userAnalytics}>
            <div className={styles.headerSection}>
                <h2 className={styles.sectionTitle}>👥 User Analytics</h2>
                
                {/* Date Range Filter */}
                <div className={styles.filterSection}>
                    <div className={styles.dateRangeButtons}>
                        <button
                            className={`${styles.rangeButton} ${dateRange === "week" ? styles.active : ""}`}
                            onClick={() => handleDateRangeChange("week")}
                        >
                            📅 This Week
                        </button>
                        <button
                            className={`${styles.rangeButton} ${dateRange === "month" ? styles.active : ""}`}
                            onClick={() => handleDateRangeChange("month")}
                        >
                            📆 This Month
                        </button>
                        <button
                            className={`${styles.rangeButton} ${dateRange === "year" ? styles.active : ""}`}
                            onClick={() => handleDateRangeChange("year")}
                        >
                            📊 This Year
                        </button>
                        <button
                            className={`${styles.rangeButton} ${dateRange === "custom" ? styles.active : ""}`}
                            onClick={() => {
                                setDateRange("custom");
                                setShowCustomDatePicker(!showCustomDatePicker);
                            }}
                        >
                            <FiCalendar /> Custom
                        </button>
                    </div>

                    {/* Custom Date Range Picker */}
                    {showCustomDatePicker && (
                        <div className={styles.customDatePicker}>
                            <div className={styles.dateInputGroup}>
                                <div className={styles.dateInput}>
                                    <label>Start Date</label>
                                    <input
                                        type="date"
                                        value={customStartDate}
                                        onChange={(e) => setCustomStartDate(e.target.value)}
                                        max={getCurrentDate()}
                                    />
                                </div>
                                <div className={styles.dateInput}>
                                    <label>End Date</label>
                                    <input
                                        type="date"
                                        value={customEndDate}
                                        onChange={(e) => setCustomEndDate(e.target.value)}
                                        max={getCurrentDate()}
                                    />
                                </div>
                            </div>
                            <div className={styles.dateActions}>
                                <button
                                    className={styles.applyButton}
                                    onClick={handleCustomDateSubmit}
                                >
                                    Apply
                                </button>
                                <button
                                    className={styles.cancelButton}
                                    onClick={() => setShowCustomDatePicker(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Date Range Display */}
                    <div className={styles.dateDisplay}>
                        📍 {startDate} to {endDate}
                    </div>
                </div>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    ⚠️ {error}
                </div>
            )}

            {/* KPI Cards */}
            <div className={styles.kpiGrid}>
                <div className={styles.kpiCard}>
                    <div className={styles.kpiIcon}>
                        <FiUsers />
                    </div>
                    <div className={styles.kpiContent}>
                        <p className={styles.kpiLabel}>Total Signups</p>
                        <p className={styles.kpiValue}>
                            {signupData?.datasets[0]?.data?.reduce((a, b) => a + b, 0) || 0}
                        </p>
                    </div>
                </div>

                <div className={styles.kpiCard}>
                    <div className={styles.kpiIcon} style={{ color: "#10b981" }}>
                        <FiTrendingUp />
                    </div>
                    <div className={styles.kpiContent}>
                        <p className={styles.kpiLabel}>Avg Daily Signups</p>
                        <p className={styles.kpiValue}>
                            {signupData?.datasets[0]?.data ? 
                                Math.round(signupData.datasets[0].data.reduce((a, b) => a + b, 0) / signupData.datasets[0].data.length) 
                            : 0}
                        </p>
                    </div>
                </div>

                <div className={styles.kpiCard}>
                    <div className={styles.kpiIcon} style={{ color: "#f59e0b" }}>
                        <FiAward />
                    </div>
                    <div className={styles.kpiContent}>
                        <p className={styles.kpiLabel}>Top Shipper</p>
                        <p className={styles.kpiValue}>{topShippers[0]?.shipperName?.split(' ')[0] || 'N/A'}</p>
                    </div>
                </div>
            </div>

            {/* Charts Grid */}
            <div className={styles.chartsGrid}>
                {/* Signup Trend Chart */}
                <div className={styles.chartCard}>
                    <h3 className={styles.chartTitle}>📈 User Signups Trend</h3>
                    {signupData ? (
                        <Chart type="line" data={signupData} options={chartOptions} />
                    ) : (
                        <p>No data available</p>
                    )}
                </div>

                {/* User Role Distribution */}
                <div className={styles.chartCard}>
                    <h3 className={styles.chartTitle}>🔍 User Role Distribution</h3>
                    {roleBreakdown ? (
                        <Chart type="pie" data={roleBreakdown} options={pieOptions} />
                    ) : (
                        <p>No data available</p>
                    )}
                </div>
            </div>

            {/* Top Shippers Table */}
            <div className={styles.tableCard}>
                <h3 className={styles.tableTitle}>🏆 Top Shippers by Weight</h3>
                {topShippers.length > 0 ? (
                    <div className={styles.tableWrapper}>
                        <table className={styles.analyticsTable}>
                            <thead>
                                <tr>
                                    <th>Rank</th>
                                    <th>Shipper Name</th>
                                    <th>Total Weight (lbs)</th>
                                    <th>Packages</th>
                                    <th>Avg Weight</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topShippers.map((shipper, idx) => (
                                    <tr key={idx} className={idx === 0 ? styles.topRow : ''}>
                                        <td>#{idx + 1}</td>
                                        <td>{shipper.shipperName}</td>
                                        <td className={styles.highlight}>{shipper.totalWeight.toLocaleString()}</td>
                                        <td>{shipper.totalPackages}</td>
                                        <td>{shipper.avgWeightPerPackage}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={styles.emptyState}>
                        <p>No shipper data available yet</p>
                    </div>
                )}
            </div>

            {/* Item Shifts Table */}
            <div className={styles.tableCard}>
                <h3 className={styles.tableTitle}>📦 Most Item Shifts</h3>
                {itemShifts.length > 0 ? (
                    <div className={styles.tableWrapper}>
                        <table className={styles.analyticsTable}>
                            <thead>
                                <tr>
                                    <th>Rank</th>
                                    <th>Courier Name</th>
                                    <th>Role</th>
                                    <th>Shifts Count</th>
                                </tr>
                            </thead>
                            <tbody>
                                {itemShifts.map((shift, idx) => (
                                    <tr key={idx}>
                                        <td>#{idx + 1}</td>
                                        <td>{shift.courierName}</td>
                                        <td>
                                            <span className={`${styles.badge} ${styles[shift.role]}`}>
                                                {shift.role}
                                            </span>
                                        </td>
                                        <td className={styles.highlight}>{shift.shiftCount}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={styles.emptyState}>
                        <p>📍 No item shift data available. Items become "shifted" when they are moved in the delivery pipeline.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserAnalytics;